import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box,
    Button,
    Typography,
    Paper,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Chip,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    StepContent,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import StorageIcon from '@mui/icons-material/Storage';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Navbar from "../components/Navbar.js";
import { backendRoot } from "../config";

export default function AppendData() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('project_id') || '';
    const fileInputRef = useRef(null);

    // State management
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [processingMessages, setProcessingMessages] = useState([]);
    const [showProcessingDialog, setShowProcessingDialog] = useState(false);
    const [processingComplete, setProcessingComplete] = useState(false);
    const [shouldAutoRefresh, setShouldAutoRefresh] = useState(false);

    const safeRender = (val) => {
        if (val === null || val === undefined) return "";
        if (typeof val === "object") {
            return val.detail || JSON.stringify(val);
        }
        return String(val);
    };

    // Form state
    const [formData, setFormData] = useState({
        selectedTable: '',
        description: ''
    });

    // Available tables
    const availableTables = [
        { value: 'intellidrop_app_buildings', label: 'intellidrop_app_buildings' },
        { value: 'intellidrop_app_parcels', label: 'intellidrop_app_parcels' },
        { value: 'intellidrop_app_roads', label: 'intellidrop_app_roads' },
        { value: 'intellidrop_app_driveway', label: 'intellidrop_app_driveway' },
        { value: 'intellidrop_app_pool', label: 'intellidrop_app_pool' }
    ];

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        const shapefiles = files.filter(file =>
            file.name.toLowerCase().endsWith('.shp') ||
            file.name.toLowerCase().endsWith('.shx') ||
            file.name.toLowerCase().endsWith('.dbf') ||
            file.name.toLowerCase().endsWith('.prj') ||
            file.name.toLowerCase().endsWith('.cpg')
        );

        if (shapefiles.length === 0) {
            setError('Please select valid shapefile files (.shp, .shx, .dbf, .prj, .cpg)');
            return;
        }

        // Group files by base name (without extension)
        const fileGroups = {};
        shapefiles.forEach(file => {
            const baseName = file.name.replace(/\.(shp|shx|dbf|prj|cpg)$/i, '');
            if (!fileGroups[baseName]) {
                fileGroups[baseName] = [];
            }
            fileGroups[baseName].push(file);
        });

        // Convert to array of file groups
        const newFileGroups = Object.values(fileGroups).map(group => ({
            id: Date.now() + Math.random(),
            files: group,
            name: group[0].name.replace(/\.(shp|shx|dbf|prj|cpg)$/i, ''),
            size: group.reduce((sum, file) => sum + file.size, 0),
            uploaded: false
        }));

        setUploadedFiles(prev => [...prev, ...newFileGroups]);
        setError(null);
    };

    const handleRemoveFile = (fileId) => {
        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    };

    // Auto refresh effect
    useEffect(() => {
        if (shouldAutoRefresh && success) {
            const timer = setTimeout(() => {
                window.location.reload();
            }, 5000); // 10 seconds
            return () => clearTimeout(timer);
        }
    }, [shouldAutoRefresh, success]);

    const handleUploadFiles = async () => {
        if (uploadedFiles.length === 0) {
            setError('Please select files to upload first');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const formData = new FormData();

            uploadedFiles.forEach((fileGroup, index) => {
                fileGroup.files.forEach(file => {
                    formData.append(`files_${index}`, file);
                });
            });

            const response = await fetch(
                `${backendRoot}/api/upload-shapefiles/`,
                {
                    method: 'POST',
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Mark files as uploaded
            setUploadedFiles(prev => prev.map(file => ({ ...file, uploaded: true })));

        } catch (error) {
            console.error('Error uploading files:', error);
            setError(`Failed to upload files: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (uploadedFiles.length === 0) {
            setError('Please upload files first');
            return;
        }

        if (!formData.selectedTable) {
            setError('Please select a destination table');
            return;
        }

        if (!formData.description.trim()) {
            setError('Please enter a description');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);
        setProcessingMessages([]);
        setShowProcessingDialog(true);
        setProcessingComplete(false);

        try {
            // Send files directly with process request to avoid cross-instance issues
            const processForm = new FormData();

            uploadedFiles.forEach((fileGroup, index) => {
                fileGroup.files.forEach(file => {
                    processForm.append(`files_${index}`, file);
                });
            });

            processForm.append('destination_table', formData.selectedTable);
            processForm.append('description', formData.description);

            // Start the processing
            const response = await fetch(
                `${backendRoot}/api/process-shapefiles/`,
                {
                    method: 'POST',
                    body: processForm, // Send FormData instead of JSON
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Handle streaming response for real-time updates
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const data = JSON.parse(line);
                            if (data.type === 'message') {
                                setProcessingMessages(prev => [...prev, data.message]);
                            } else if (data.type === 'complete') {
                                setProcessingComplete(true);
                                setSuccess(true);
                            } else if (data.type === 'error') {
                                throw new Error(data.message);
                            }
                        } catch (e) {
                            // Handle non-JSON lines as regular messages
                            if (line.trim()) {
                                setProcessingMessages(prev => [...prev, line.trim()]);
                            }
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Error processing files:', error);
            setError(`Failed to process files: ${error.message}`);
            setProcessingMessages(prev => [...prev, `Error: ${error.message}`]);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseProcessingDialog = () => {
        setShowProcessingDialog(false);
        setProcessingMessages([]);
        setProcessingComplete(false);

        if (success) {
            // Start auto refresh countdown
            setShouldAutoRefresh(true);

            // Reset form on successful completion
            setUploadedFiles([]);
            setFormData({
                selectedTable: '',
                description: ''
            });
        }
    };

    const handleBackToTransactions = () => {
        navigate(
            projectId
                ? `/transactions?project_id=${encodeURIComponent(projectId)}`
                : '/transactions'
        );
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box sx={{ background: "#f8f9fa", minHeight: "100vh" }}>
            <Navbar />

            {/* Spacing below navbar */}
            <Box sx={{ py: 2 }} />

            {/* Header with Back Button and Reports Button */}
            <Box sx={{
                px: { xs: 2, md: 4 },
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToTransactions}
                    sx={{
                        background: '#60A5FA',
                        color: '#fff',
                        '&:hover': { background: '#3B82F6' },
                        fontWeight: 600,
                        mb: 2,
                        borderRadius: 2
                    }}
                >
                    Back to Transactions
                </Button>

                <Button
                    onClick={() => navigate('/reports')}
                    sx={{
                        background: '#60A5FA',
                        color: '#fff',
                        '&:hover': { background: '#3B82F6' },
                        fontWeight: 600,
                        mb: 2,
                        borderRadius: 2,
                        px: 3
                    }}
                >
                    Reports
                </Button>
            </Box>

            {/* Main Content Container */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                px: { xs: 2, md: 4 },
                pb: 4
            }}>
                <Card sx={{
                    maxWidth: 800,
                    width: '100%',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)'
                }}>
                    <CardContent sx={{ p: 4 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                                {safeRender(error)}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(false)}>
                                Data uploaded and processed successfully!
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            {/* Upload File Section */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{
                                    fontWeight: 700,
                                    color: '#2c3e50',
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <UploadFileIcon sx={{ color: '#34495e' }} />
                                    Upload File
                                </Typography>

                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    p: 2,
                                    border: '2px dashed #e0e6ed',
                                    borderRadius: 2,
                                    backgroundColor: '#fafbfc',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: '#3498db',
                                        backgroundColor: '#f8f9fa'
                                    }
                                }}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept=".shp,.shx,.dbf,.prj,.cpg"
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                    <TextField
                                        variant="outlined"
                                        placeholder="Browse for shapefile files..."
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'white',
                                                cursor: 'pointer',
                                                '&:hover fieldset': { borderColor: '#3498db' },
                                                '&.Mui-focused fieldset': { borderColor: '#3498db' },
                                            }
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            startAdornment: <FolderOpenIcon sx={{ color: '#7f8c8d', mr: 1 }} />,
                                        }}
                                    />

                                    <Button
                                        variant="contained"
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            backgroundColor: '#3498db',
                                            color: 'white',
                                            '&:hover': { backgroundColor: '#2980b9' },
                                            fontWeight: 600,
                                            px: 3,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        Browse
                                    </Button>
                                </Box>

                                {/* Uploaded Files List */}
                                {uploadedFiles.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#2c3e50' }}>
                                            Selected Files ({uploadedFiles.length})
                                        </Typography>
                                        {/* Overall totals */}
                                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={`Total groups: ${uploadedFiles.length}`}
                                                size="small"
                                                sx={{ backgroundColor: '#e8f4fd', color: '#2980b9' }}
                                            />
                                            <Chip
                                                label={`Total files: ${uploadedFiles.reduce((sum, g) => sum + g.files.length, 0)}`}
                                                size="small"
                                                sx={{ backgroundColor: '#e8f4fd', color: '#2980b9' }}
                                            />
                                            <Chip
                                                label={`Total size: ${formatFileSize(uploadedFiles.reduce((sum, g) => sum + g.size, 0))}`}
                                                size="small"
                                                sx={{ backgroundColor: '#fff3cd', color: '#856404' }}
                                            />
                                        </Box>
                                        <List sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 1 }}>
                                            {uploadedFiles.map((fileGroup) => (
                                                <ListItem
                                                    key={fileGroup.id}
                                                    sx={{
                                                        border: '1px solid #e9ecef',
                                                        borderRadius: 2,
                                                        mb: 1,
                                                        backgroundColor: 'white',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        {fileGroup.uploaded ? (
                                                            <CheckCircleIcon sx={{ color: '#27ae60' }} />
                                                        ) : (
                                                            <InfoIcon sx={{ color: '#95a5a6' }} />
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={fileGroup.name}
                                                        secondary={
                                                            <Box sx={{ mt: 1 }}>
                                                                {/* Group summary chips */}
                                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                                    <Chip
                                                                        label={`${fileGroup.files.length} files`}
                                                                        size="small"
                                                                        sx={{ backgroundColor: '#e8f4fd', color: '#2980b9' }}
                                                                    />
                                                                    <Chip
                                                                        label={formatFileSize(fileGroup.size)}
                                                                        size="small"
                                                                        sx={{ backgroundColor: '#fff3cd', color: '#856404' }}
                                                                    />
                                                                    {fileGroup.uploaded && (
                                                                        <Chip
                                                                            label="Uploaded"
                                                                            size="small"
                                                                            sx={{ backgroundColor: '#d4edda', color: '#155724' }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                                {/* Filenames list */}
                                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                    {fileGroup.files.map((f, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={f.name}
                                                                            size="small"
                                                                            sx={{ backgroundColor: '#eef2f7', color: '#2c3e50' }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        }
                                                    />
                                                    <IconButton
                                                        onClick={() => handleRemoveFile(fileGroup.id)}
                                                        disabled={fileGroup.uploaded}
                                                        size="small"
                                                        sx={{ color: '#e74c3c' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </Box>

                            {/* Select Table Section */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{
                                    fontWeight: 700,
                                    color: '#2c3e50',
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <StorageIcon sx={{ color: '#34495e' }} />
                                    Select Table
                                </Typography>

                                <FormControl fullWidth>
                                    <InputLabel>Destination Table</InputLabel>
                                    <Select
                                        value={formData.selectedTable}
                                        onChange={handleInputChange('selectedTable')}
                                        input={<OutlinedInput label="Destination Table" />}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover fieldset': { borderColor: '#3498db' },
                                                '&.Mui-focused fieldset': { borderColor: '#3498db' },
                                            }
                                        }}
                                    >
                                        {availableTables.map((table) => (
                                            <MenuItem key={table.value} value={table.value}>
                                                {table.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Description Section */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{
                                    fontWeight: 700,
                                    color: '#2c3e50',
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <DescriptionIcon sx={{ color: '#34495e' }} />
                                    Description
                                </Typography>

                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={formData.description}
                                    onChange={handleInputChange('description')}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    placeholder="Enter a description for this data upload..."
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': { borderColor: '#3498db' },
                                            '&.Mui-focused fieldset': { borderColor: '#3498db' },
                                        }
                                    }}
                                />
                            </Box>

                            {/* Submit Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading || uploadedFiles.length === 0 || !formData.selectedTable || !formData.description.trim()}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                    sx={{
                                        backgroundColor: '#27ae60',
                                        color: 'white',
                                        '&:hover': { backgroundColor: '#229954' },
                                        '&:disabled': { backgroundColor: '#bdc3c7', color: '#7f8c8d' },
                                        fontWeight: 600,
                                        px: 6,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '1.1rem',
                                        boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)'
                                    }}
                                >
                                    {loading ? 'Processing...' : 'Submit'}
                                </Button>
                            </Box>
                        </form>
                    </CardContent>
                </Card>
            </Box>

            {/* Processing Status Dialog */}
            <Dialog
                open={showProcessingDialog}
                onClose={processingComplete ? handleCloseProcessingDialog : undefined}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: '#3498db',
                    color: 'white',
                    borderRadius: '12px 12px 0 0'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {processingComplete ? (
                            <CheckCircleIcon />
                        ) : (
                            <CircularProgress size={24} color="inherit" />
                        )}
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {processingComplete ? 'Processing Complete' : 'Processing...'}
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Paper
                        sx={{
                            p: 3,
                            backgroundColor: '#f8f9fa',
                            maxHeight: 400,
                            overflow: 'auto',
                            border: '1px solid #e9ecef',
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
                            Output messages from the Script being run:
                        </Typography>

                        {processingMessages.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Waiting for processing messages...
                            </Typography>
                        ) : (
                            <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                {processingMessages.map((message, index) => (
                                    <Box key={index} sx={{ mb: 1, color: '#2c3e50' }}>
                                        {safeRender(message)}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {!processingComplete && (
                            <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />
                        )}
                    </Paper>
                </DialogContent>

                {processingComplete && (
                    <DialogActions sx={{ p: 3 }}>
                        <Button
                            onClick={handleCloseProcessingDialog}
                            variant="contained"
                            sx={{
                                backgroundColor: '#27ae60',
                                color: 'white',
                                '&:hover': { backgroundColor: '#229954' },
                                fontWeight: 600,
                                px: 4,
                                borderRadius: 2,
                                textTransform: 'none'
                            }}
                        >
                            Done
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        </Box>
    );
}
